import accidentalCatalogPublish from "../../../../docs/operations/runbooks/accidental-catalog-publish.md?raw";
import notificationFailure from "../../../../docs/operations/runbooks/notification-failure.md?raw";
import paymentReconcile from "../../../../docs/operations/runbooks/payment-reconcile.md?raw";
import privacyCleanupFailure from "../../../../docs/operations/runbooks/privacy-cleanup-failure.md?raw";
import providerOutage from "../../../../docs/operations/runbooks/provider-outage.md?raw";
import readme from "../../../../docs/operations/runbooks/README.md?raw";
import slaEscalationStorm from "../../../../docs/operations/runbooks/sla-escalation-storm.md?raw";
import staffCompromiseOffboarding from "../../../../docs/operations/runbooks/staff-compromise-offboarding.md?raw";
import stuckJob from "../../../../docs/operations/runbooks/stuck-job.md?raw";
import webhookSignatureFailures from "../../../../docs/operations/runbooks/webhook-signature-failures.md?raw";

// The operational runbooks (CHT-772), bundled into the admin from `docs/operations/runbooks/*.md`. The text in the repository is the only copy:
// the admin imports it as text, so a runbook and the page that links to it cannot drift apart. Each diagnostic shows a link to its section;
// `runbooks.test.ts` fails when a link names a runbook or a section that does not exist.

export const runbookSources: Readonly<Record<string, string>> = {
  index: readme,
  "provider-outage": providerOutage,
  "stuck-job": stuckJob,
  "payment-reconcile": paymentReconcile,
  "notification-failure": notificationFailure,
  "webhook-signature-failures": webhookSignatureFailures,
  "sla-escalation-storm": slaEscalationStorm,
  "privacy-cleanup-failure": privacyCleanupFailure,
  "accidental-catalog-publish": accidentalCatalogPublish,
  "staff-compromise-offboarding": staffCompromiseOffboarding,
};

/** The nine incident runbooks, in the order of the index. */
export const incidentRunbooks = [
  "provider-outage",
  "stuck-job",
  "payment-reconcile",
  "notification-failure",
  "webhook-signature-failures",
  "sla-escalation-storm",
  "privacy-cleanup-failure",
  "accidental-catalog-publish",
  "staff-compromise-offboarding",
] as const;

/** The anchor a heading gets: lower case, letters, digits and hyphens (`Recover (supported actions)` is `recover-supported-actions`). */
export function slugOf(heading: string): string {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, "")
    .trim()
    .replace(/ +/g, "-");
}

/** The anchors of a runbook, from its headings. */
export function anchorsOf(slug: string): readonly string[] {
  const source = runbookSources[slug];
  if (source === undefined) return [];
  return source
    .split("\n")
    .map((line) => /^#{1,3} +(.+?) *$/.exec(line)?.[1])
    .filter((heading): heading is string => heading !== undefined)
    .map(slugOf);
}

/** The title of a runbook: its first heading without the word "Runbook:". */
export function titleOf(slug: string): string {
  const first = (runbookSources[slug] ?? "").split("\n").find((line) => line.startsWith("# "));
  return (first ?? slug).replace(/^# +/, "").replace(/^Runbook: */i, "");
}

/** A runbook section the diagnostics link to. `section` is an anchor of the runbook, or `null` for the top. */
export type RunbookRef = Readonly<{ slug: string; section: string | null }>;

/** The in-app address of a runbook section. */
export const runbookPath = (ref: RunbookRef): string =>
  `/operations/runbooks/${ref.slug}${ref.section === null ? "" : `#${ref.section}`}`;

// --- the links of the diagnostics -----------------------------------------------------------------------------------------------

/** The section of the provider-outage runbook of each provider. */
export const providerRunbook: Readonly<Record<string, RunbookRef>> = {
  stream: { slug: "provider-outage", section: "stream" },
  trigger: { slug: "provider-outage", section: "triggerdev" },
  razorpay: { slug: "provider-outage", section: "razorpay" },
  google: { slug: "provider-outage", section: "google" },
  expo: { slug: "provider-outage", section: "expo-push" },
  model: { slug: "provider-outage", section: "model-providers" },
  resend: { slug: "provider-outage", section: "resend" },
  supabase: { slug: "provider-outage", section: "supabase" },
};

export const runbooks = {
  providerOutage: { slug: "provider-outage", section: "read-the-status-first" },
  stuckJob: { slug: "stuck-job", section: "recover-supported-actions" },
  paymentReconcile: { slug: "payment-reconcile", section: "reconcile-one-payment" },
  paymentException: { slug: "payment-reconcile", section: "resolve-an-exception" },
  notificationFailure: { slug: "notification-failure", section: "check" },
  webhookSignature: { slug: "webhook-signature-failures", section: "check" },
  slaStorm: { slug: "sla-escalation-storm", section: "recover-supported-actions" },
  privacyCleanup: { slug: "privacy-cleanup-failure", section: "check" },
  catalogPublish: { slug: "accidental-catalog-publish", section: "recover-supported-actions" },
  staffCompromise: { slug: "staff-compromise-offboarding", section: "recover-supported-actions" },
} as const satisfies Record<string, RunbookRef>;
